import React, { useRef, useEffect, useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  SignInButton,
  SignedIn,
  SignedOut,
  UserButton,
  useUser,
} from "@clerk/clerk-react";
import { fetchDistricts } from "../api/api";
import { fetchIndustries } from "../api/industryApi";
import MobileHeader from "./MobileHeader";

const NewHeader = () => {
  const navRef = useRef(null);
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const [isAdmin, setIsAdmin] = useState(false);
  const lastToggleRef = useRef(0);

  // On mobile, expanding a dropdown shifts everything below it down. A
  // fast follow-up tap can occasionally register against the browser's
  // pre-reflow hit-test coordinates and land on whatever sibling item
  // used to occupy that spot (e.g. tapping a just-revealed Policy Library
  // submenu item misfiring as a click on Investors). Swallow any click
  // that happens immediately after a toggle so a stray mis-hit can't
  // trigger a real navigation — a genuine follow-up tap still works fine.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const guardStaleClick = (e) => {
      if (Date.now() - lastToggleRef.current < 350) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    nav.addEventListener("click", guardStaleClick, true);
    return () => nav.removeEventListener("click", guardStaleClick, true);
  }, []);

  useEffect(() => {
    if (isLoaded && user) {
      const userRole = user.publicMetadata?.role || "";
      setIsAdmin(["admin", "superadmin"].includes(userRole));
    } else {
      setIsAdmin(false);
    }
  }, [user, isLoaded]);

  const positionPanel = useCallback((panel, triggerEl) => {
    if (!navRef.current || !panel) return;

    // On mobile, responsive.css forces the mega-panel to a static, full-
    // width, in-flow block. Setting any inline positioning here would
    // fight that (and can leave stray styles that overlap/cover the
    // rest of the mobile menu), so skip it entirely below the desktop
    // breakpoint and let CSS fully own the mobile layout.
    if (window.innerWidth <= 991) return;

    const navRect = navRef.current.getBoundingClientRect();
    const top = Math.round(navRect.bottom + 2);

    panel.style.position = "fixed";
    panel.style.top = `${top}px`;
    panel.style.zIndex = "1000";
    panel.style.backgroundColor = "#fff";
    panel.style.boxShadow = "0 5px 15px rgba(0,0,0,0.1)";
    panel.style.borderRadius = "4px";
    panel.style.padding = "18px";
    panel.style.overflowX = "hidden";
    panel.style.overflowY = "auto";

    if (triggerEl) {
      // Narrower panels (e.g. Districts) should sit under their own
      // trigger rather than centered on the whole viewport, so they
      // don't visually drift away from the link that opened them.
      const triggerRect = triggerEl.getBoundingClientRect();
      const panelWidth = panel.getBoundingClientRect().width || 480;
      let left = triggerRect.left + triggerRect.width / 2 - panelWidth / 2;
      // Keep it fully on-screen with a small margin.
      left = Math.max(12, Math.min(left, window.innerWidth - panelWidth - 12));
      panel.style.left = `${Math.round(left)}px`;
      panel.style.transform = "none";
    } else {
      panel.style.left = "50%";
      panel.style.transform = "translateX(-50%)";
      panel.style.width = "min(85vw, 1200px)";
      panel.style.maxWidth = "90vw";
    }
  }, [navRef]);

  const [openMenu, setOpenMenu] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const districtMenuRef = useRef(null);
  const industryMenuRef = useRef(null);
  const megaMenuCloseTimeoutRef = useRef(null);

  // The mega-panel is position:fixed, centered under the whole nav rather
  // than directly beneath its trigger — so moving the mouse from the
  // trigger down to the panel crosses a gap that isn't part of either
  // element, firing mouseleave mid-transit and closing the panel before a
  // click can land. Give a short grace period instead of closing instantly,
  // and cancel it if the mouse re-enters the trigger or the panel.
  const openMegaMenu = (name) => {
    if (megaMenuCloseTimeoutRef.current) {
      clearTimeout(megaMenuCloseTimeoutRef.current);
      megaMenuCloseTimeoutRef.current = null;
    }
    setOpenMenu(name);
  };

  const scheduleCloseMegaMenu = (name) => {
    if (megaMenuCloseTimeoutRef.current) {
      clearTimeout(megaMenuCloseTimeoutRef.current);
    }
    megaMenuCloseTimeoutRef.current = setTimeout(() => {
      setOpenMenu((prev) => (prev === name ? null : prev));
    }, 250);
  };

  useEffect(() => {
    return () => {
      if (megaMenuCloseTimeoutRef.current) {
        clearTimeout(megaMenuCloseTimeoutRef.current);
      }
    };
  }, []);

  const [districts, setDistricts] = useState([]);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);

  // Fetch districts on component mount
  useEffect(() => {
    const loadDistricts = async () => {
      try {
        setDistrictsLoading(true);
        const response = await fetchDistricts();
        if (response.success) {
          setDistricts(response.data);
        }
      } catch (error) {
        console.error("Error fetching districts:", error);
      } finally {
        setDistrictsLoading(false);
      }
    };

    loadDistricts();
  }, []);

  // Fetch industries on component mount
  useEffect(() => {
    const loadIndustries = async () => {
      try {
        setIndustriesLoading(true);
        const response = await fetchIndustries();

        if (response && response.success && response.data) {
          setIndustries(response.data);
        } else if (response && response.industries) {
          setIndustries(response.industries);
        } else if (Array.isArray(response)) {
          setIndustries(response);
        } else {
          console.error("Unexpected industries response format:", response);
          setIndustries([]);
        }
      } catch (error) {
        console.error("Error fetching industries:", error);
        setIndustries([]);
      } finally {
        setIndustriesLoading(false);
      }
    };

    loadIndustries();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideDistrictMenu =
        districtMenuRef.current &&
        !districtMenuRef.current.contains(event.target);
      const isOutsideIndustryMenu =
        industryMenuRef.current &&
        !industryMenuRef.current.contains(event.target);

      if (openMenu === "district" && isOutsideDistrictMenu) {
        setOpenMenu(null);
      } else if (openMenu === "industry" && isOutsideIndustryMenu) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenu]);

  const toggleMenu = (menu) => {
    setOpenMenu(openMenu === menu ? null : menu);
    // Keep only one dropdown open at a time on mobile so panels can't
    // stack on top of each other and cover the items below them.
    setOpenSubmenu(null);
    lastToggleRef.current = Date.now();
  };

  const closeMenu = () => {
    setOpenMenu(null);
    setIsMenuOpen(false);
    setOpenSubmenu(null);
  };

  const toggleSubmenu = (name) => {
    setOpenSubmenu(openSubmenu === name ? null : name);
    setOpenMenu(null);
    lastToggleRef.current = Date.now();
  };

  // Position the panel when it becomes visible
  useEffect(() => {
    if (!navRef.current || !openMenu) return;
    const activePanel = navRef.current.querySelector(
      openMenu === "district"
        ? "li.district-menu .mega-panel"
        : openMenu === "industry"
        ? "li.industries-menu .mega-panel"
        : null
    );
    // District's narrow panel aligns under its own trigger; Industry
    // keeps its existing viewport-centered wide layout (no trigger passed).
    const trigger = openMenu === "district" ? districtMenuRef.current : null;
    if (activePanel) positionPanel(activePanel, trigger);
  }, [openMenu, positionPanel, navRef]);

  useEffect(() => {
    const onResize = () => {
      const districtPanel = navRef.current?.querySelector(
        "li.district-menu .mega-panel"
      );
      const industryPanel = navRef.current?.querySelector(
        "li.industries-menu .mega-panel"
      );
      [
        [districtPanel, districtMenuRef.current],
        [industryPanel, null],
      ].forEach(([p, trigger]) => {
        if (!p) return;
        const style = window.getComputedStyle(p);
        const visible =
          style.display !== "none" && parseFloat(style.opacity || "0") > 0;
        if (visible) positionPanel(p, trigger);
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [positionPanel, navRef]);

  const menuItems = [
    {
      name: "Policy Library",
      submenu: [
        "policy-advocacy",
        "scheme",
        {
          label: "Government Policy",
          submenu: [
            { path: "maha-gov-policy", label: "Maharashtra Government" },
            { path: "cent-gov-policy", label: "Central Government" },
          ],
        },
      ],
    },
    { name: "Investors", to: "/investors" },
    {
      name: "Innovator",
      submenu: ["startup", "incubator", "association", "investor", "academia", "consultant"],
    },
    {
      name: "Media",
      submenu: [
        "media-room",
        "press-release",
        "blog",
      ],
    },
    {
      name: "About Us",
      submenu: [
        { path: "about-aid", label: "About AID" },
        { path: "vision-mission", label: "Vision & Mission" },
        { path: "members", label: "Executive Committee" },
        { path: "contactusform", label: "Contact Us" },
      ],
    },
    {
      name: "VGF",
      to: "/vidarbha-global-foundation",
      isImage: true,
      imagePath: "/img/VGF.svg",
    },
    {
      name: "Advantage Vidarbha",
      externalLink: "https://advantagevidarbha.in/",
      openInNewTab: true,
      isImage: true,
      imagePath: "/img/AV.svg",
    },
    { name: "Join Us", to: "/login" },
  ];

  return (
    <>
      <style>{`
        /* Bootstrap's .container class (loaded globally in index.html)
           sets a FIXED width per breakpoint (750px/970px/1170px) instead
           of a percentage — so on any screen wider than that breakpoint's
           fixed size, the nav's container stops growing and sits flush
           left, leaving blank space on the right where the purple banner
           and slider (which don't use .container) keep spanning full
           width. Force it to actually fill the nav bar edge-to-edge. */
        .header-navigation .container {
          width: 100% !important;
          max-width: 100% !important;
        }

        /* ul.navigation-box itself now correctly stretches to fill the
           full-width container (via its own flex:1), but its <li> children
           default to left-aligned (justify-content isn't set), so they
           stay bunched on the left instead of spreading across that space.
           Spread them out so "Join Us" lands at the right edge. Desktop
           only — on mobile the list becomes a vertical column, where
           space-between would create huge vertical gaps instead. */
        @media (min-width: 992px) {
          .header-navigation ul.navigation-box {
            justify-content: space-between !important;
          }
        }

        /* Districts only has ~11 items, but shares the same wide
           4-column .mega-panel/.mega-grid as Industry (which has many
           more) — leaving lots of empty space and far-apart items.
           Scope a more compact size to Districts only so Industry's
           panel is completely unaffected. */
        @media (min-width: 992px) {
          .district-menu .mega-panel {
            width: 480px !important;
            max-width: 90vw !important;
          }
          .district-menu .mega-grid {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 6px !important;
          }
          .district-menu .mega-item {
            padding: 8px 10px !important;
          }
          .district-menu .mega-panel-inner {
            padding: 8px !important;
          }
        }

        .has-nested-submenu:hover > .nested-sub-menu {
          opacity: 1 !important;
          visibility: visible !important;
        }

        .nested-sub-menu a {
          color: #333 !important;
        }

        .nested-sub-menu a:hover {
          background-color: #470172;
          color: #fff !important;
        }

        /* The "home-one" header variant's base CSS never sets
           .navbar-header to display:none by default (only position),
           so without this it would float over the desktop nav too.
           responsive.css's own @media (max-width: 991px) rule already
           reveals it with !important, so this only affects >991px. */
        .navbar-header {
          display: none;
        }

        .mobile-logo {
          display: none;
        }

        /* Desktop already reveals submenus on hover — the chevron is a
           mobile-only affordance since tapping is the only way there. */
        .submenu-chevron {
          display: none;
        }

        /* Below the desktop breakpoint, MobileHeader.js (a fully separate
           component with its own state and markup) owns navigation
           entirely. Hide this component's desktop nav so the two never
           overlap or fight each other. */
        @media (max-width: 991px) {
          .header-top {
            display: none !important;
          }
          .header.header-home-one {
            top: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .header-navigation {
            display: none !important;
          }

          /* Slider heading/subtitle text runs a bit large on mobile —
             trim it down slightly from the template's own sizing. */
          .main-slider h2 {
            font-size: 26px !important;
          }
          .main-slider .text {
            font-size: 14px !important;
          }
        }

        @media (max-width: 599px) {
          .main-slider h2 {
            font-size: 20px !important;
          }
          .main-slider .text {
            font-size: 12px !important;
          }
        }
      `}</style>
      {/* Header Top Section */}
      <div className="header-top home-one clearfix">
        <div className="container">
          <div className="logo pull-left">{/* Logo content */}</div>
          <div className="header-right-info pull-right">
            {/* Header info content */}
          </div>
        </div>
      </div>

      {/* Main Header Navigation */}
      <header
        className="header header-home-one clearfix"
        style={{ minHeight: "auto" }}
      >
        <nav
          ref={navRef}
          className="navbar navbar-default header-navigation stricky clearfix"
          style={{ minHeight: "60px", padding: "5px 0" }}
        >
          <div
            className="container clearfix"
            style={{ maxWidth: "100%", padding: "0 15px" }}
          >
            <Link to="/" className="mobile-logo" onClick={() => setIsMenuOpen(false)}>
              <img src="/img/aid.svg" alt="AID" />
            </Link>
            <div className="navbar-header">
              <button
                type="button"
                className={`side-nav-toggler ${isMenuOpen ? "toggler-open" : ""}`}
                aria-label="Toggle menu"
                aria-expanded={isMenuOpen}
                onClick={() => setIsMenuOpen((prev) => !prev)}
              >
                {isMenuOpen ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </svg>
                )}
              </button>
            </div>
            <ul className={`nav navbar-nav navigation-box ${isMenuOpen ? "mobile-menu-open" : ""}`}>
              <li className="current">
                <Link to="/" onClick={() => setIsMenuOpen(false)}>Home</Link>
              </li>

              <li>
                <Link to="/vidarbha" onClick={() => setIsMenuOpen(false)}>Vidarbha Corridor</Link>
              </li>

              {/* District Menu */}
              <li
                className="has-submenu mega district-menu"
                ref={districtMenuRef}
                onMouseEnter={() => { if (window.innerWidth > 991) openMegaMenu("district"); }}
                onMouseLeave={() => { if (window.innerWidth > 991) scheduleCloseMegaMenu("district"); }}
              >
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    // Desktop reveals this on hover — only toggle via
                    // click on mobile, where there's no hover.
                    if (window.innerWidth <= 991) {
                      toggleMenu("district");
                    }
                  }}
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 24 }}
                >
                  Districts
                  <svg
                    className="submenu-chevron"
                    width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transition: "transform 0.2s ease", transform: openMenu === "district" ? "rotate(180deg)" : "rotate(0deg)" }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </a>
                <div
                  className="mega-panel"
                  onMouseEnter={() => { if (window.innerWidth > 991) openMegaMenu("district"); }}
                  onMouseLeave={() => { if (window.innerWidth > 991) scheduleCloseMegaMenu("district"); }}
                  style={{
                    display: openMenu === "district" ? "block" : "none",
                    opacity: openMenu === "district" ? 1 : 0,
                    transition: "all 0.3s ease",
                    pointerEvents: openMenu === "district" ? "auto" : "none",
                  }}
                >
                  <div className="mega-panel-inner">
                    <div className="mega-grid">
                      {districtsLoading ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-600 mx-auto mb-2"></div>
                          <p className="text-sm text-gray-600">
                            Loading districts...
                          </p>
                        </div>
                      ) : districts.length > 0 ? (
                        districts.map((district) => (
                          <Link
                            key={district._id}
                            className="mega-item"
                            to={`/district/${district._id}`}
                            onClick={closeMenu}
                          >
                            {district.districtName}
                          </Link>
                        ))
                      ) : (
                        <p className="text-sm text-gray-600 py-4">
                          No districts available
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </li>

              {/* Industry Menu */}
              <li
                className="has-submenu mega industries-menu"
                ref={industryMenuRef}
                onMouseEnter={() => { if (window.innerWidth > 991) openMegaMenu("industry"); }}
                onMouseLeave={() => { if (window.innerWidth > 991) scheduleCloseMegaMenu("industry"); }}
              >
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    // Desktop reveals this on hover — only toggle via
                    // click on mobile, where there's no hover.
                    if (window.innerWidth <= 991) {
                      toggleMenu("industry");
                    }
                  }}
                  style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 24 }}
                >
                  Industry
                  <svg
                    className="submenu-chevron"
                    width="16" height="16" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transition: "transform 0.2s ease", transform: openMenu === "industry" ? "rotate(180deg)" : "rotate(0deg)" }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </a>
                <div
                  className="mega-panel"
                  onMouseEnter={() => { if (window.innerWidth > 991) openMegaMenu("industry"); }}
                  onMouseLeave={() => { if (window.innerWidth > 991) scheduleCloseMegaMenu("industry"); }}
                  style={{
                    display: openMenu === "industry" ? "block" : "none",
                    opacity: openMenu === "industry" ? 1 : 0,
                    transition: "all 0.3s ease",
                    pointerEvents: openMenu === "industry" ? "auto" : "none",
                  }}
                >
                  <div className="mega-panel-inner">
                    <div className="mega-grid">
                      {industriesLoading ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-purple-600 mx-auto mb-2"></div>
                          <p className="text-sm text-gray-600">
                            Loading industries...
                          </p>
                        </div>
                      ) : industries.length > 0 ? (
                        industries.map((industry) => (
                          <Link
                            key={industry._id}
                            className="mega-item"
                            to={`/industrypage/${industry._id}`}
                            onClick={closeMenu}
                          >
                            {industry.sectorName ||
                              industry.name ||
                              "Unnamed Industry"}
                          </Link>
                        ))
                      ) : (
                        <p className="text-sm text-gray-600 py-4">
                          No industries available
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </li>

              {menuItems.map((item) => (
                <li
                  key={item.name}
                  className={openSubmenu === item.name ? "menu-open" : ""}
                >
                  {item.submenu ? (
                    <>
                      <Link
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          // Desktop already reveals the submenu on hover —
                          // toggling here would force the mobile-only
                          // inline positioning override, breaking the
                          // CSS-driven dropdown placement. Only toggle on
                          // mobile widths, where there's no hover.
                          if (window.innerWidth <= 991) {
                            toggleSubmenu(item.name);
                          }
                        }}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 24 }}
                      >
                        {item.name}
                        <svg
                          className="submenu-chevron"
                          width="16" height="16" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          style={{ transition: "transform 0.2s ease", transform: openSubmenu === item.name ? "rotate(180deg)" : "rotate(0deg)" }}
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </Link>

                      <ul
                        className="sub-menu"
                        style={
                          openSubmenu === item.name
                            ? { display: "block", position: "static", opacity: 1, visibility: "visible" }
                            : undefined
                        }
                      >
                        {item.submenu.map((sub, subIndex) => {
                          // Check if this submenu item has nested submenu
                          if (sub.submenu) {
                            const nestedKey = `${item.name.toLowerCase()}-${sub.label.toLowerCase().replace(/\s+/g, '-')}`;

                            return (
                              <li
                                key={nestedKey}
                                className="has-nested-submenu"
                                style={{ position: "relative" }}
                              >
                                <Link
                                  to="#"
                                  onClick={(e) => e.preventDefault()}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  {sub.label}
                                  <svg
                                    width="10"
                                    height="10"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{ marginLeft: "8px" }}
                                  >
                                    <polyline points="9 6 15 12 9 18"></polyline>
                                  </svg>
                                </Link>
                                <ul
                                  className="nested-sub-menu"
                                  style={{
                                    position: "absolute",
                                    left: "100%",
                                    top: "0",
                                    backgroundColor: "#fff",
                                    minWidth: "200px",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                    borderRadius: "4px",
                                    padding: "8px 0",
                                    opacity: 0,
                                    visibility: "hidden",
                                    transition: "opacity 0.3s ease, visibility 0.3s ease",
                                  }}
                                >
                                  {sub.submenu.map((nestedSub) => (
                                    <li key={nestedSub.path}>
                                      <Link
                                        to={`/${nestedSub.path}`}
                                        onClick={() => setIsMenuOpen(false)}
                                        style={{
                                          display: "block",
                                          padding: "8px 16px",
                                          color: "#333",
                                          textDecoration: "none",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {nestedSub.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </li>
                            );
                          }

                          // Regular submenu item without nested submenu
                          const subPath = typeof sub === "string" ? sub : sub.path;
                          const subLabel = typeof sub === "string"
                            ? sub.split("-").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
                            : sub.label;

                          const finalPath = `/${
                            subPath.includes("/")
                              ? subPath
                              : item.name.toLowerCase() === "about us" ||
                                item.name.toLowerCase() === "policy library" ||
                                item.name.toLowerCase() === "media"
                              ? subPath
                              : `${item.name.toLowerCase()}/${subPath}`
                          }`;

                          return (
                            <li key={subPath || subIndex}>
                              <Link to={finalPath} onClick={() => setIsMenuOpen(false)}>{subLabel}</Link>
                            </li>
                          );
                        })}
                      </ul>
                    </>
                  ) : item.externalLink ? (
                    <a
                      href={item.externalLink}
                      target={item.openInNewTab ? "_blank" : "_self"}
                      rel={
                        item.openInNewTab ? "noopener noreferrer" : undefined
                      }
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      {item.isImage ? (
                        <img
                          src={item.imagePath}
                          alt={item.name}
                          style={{
                            height: "40px",
                            width: "auto",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <span
                          style={
                            item.name === "Join Us"
                              ? {
                                  fontSize: "20px",
                                  fontWeight: "500",
                                  color: "#ffffff",
                                  backgroundColor: "#672f89",
                                }
                              : {}
                          }
                        >
                          {item.name}
                        </span>
                      )}
                    </a>
                  ) : (
                    <Link
                      to={item.to}
                      onClick={() => setIsMenuOpen(false)}
                      className={
                        item.name === "Join Us" ? "join-us-link" : ""
                      }
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      {item.isImage ? (
                        <img
                          src={item.imagePath}
                          alt={item.name}
                          style={{
                            height: "40px",
                            width: "auto",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        item.name
                      )}
                    </Link>
                  )}
                </li>
              ))}

              {isAdmin && (
                <li>
                  <Link to="/admin" onClick={() => setIsMenuOpen(false)} className="text-yellow-400 font-medium">
                    Admin Panel
                  </Link>
                </li>
              )}
            </ul>
          </div>
        </nav>
      </header>

      <MobileHeader
        menuItems={menuItems}
        districts={districts}
        districtsLoading={districtsLoading}
        industries={industries}
        industriesLoading={industriesLoading}
        isAdmin={isAdmin}
      />
    </>
  );
};

export default NewHeader;
